import type { CropInfo } from '@lumapps/focal-point/types';
import type { ColorPalette, ColorVariant, FocusPoint } from '@lumapps/lumx/react';
import type { TranslatableObject, TranslateObject } from '@lumapps/translations';
import type { MinimumViableUser } from '@lumapps/user/types';
import type { UserReference } from '@lumapps/widget-base/types';

/**
 * Base interface for common file/media content properties.
 */
export interface BaseFileContent {
    /** The url for downloading the file. */
    downloadUrl?: string;
    /** The file extension. */
    ext?: string;
    /** The unique identifier of the file. */
    fileId?: string;
    /** Information about the focal point of an image. */
    focalPoint?: FocusPoint;
    /** The height of the file. This is only applicable for images. */
    height?: number;
    /** The file language. */
    lang?: string;
    /** The MIME type of the file. */
    mimeType?: string;
    /** The name of the file. */
    name?: string;
    /** Information about the file owner. */
    owner: MinimumViableUser<'email' | 'fullName'> & {
        userId: string;
    };
    /** The url to use when embedding a document (typically an image) in a web page. */
    servingUrl?: string;
    /** The size of the file in bytes. */
    size?: number;
    /** The file type (e.g., document or image). More specific types can be used in implementing interfaces. */
    type?: string;
    /** The URL of the file. */
    url?: string;
    /** The blob key of the file or a similar backend identifier for the file's content. */
    value?: string;
    /** The width of the file. This is only applicable for images. */
    width?: number;
}

export enum MediaType {
    IMAGE = 'image',
    DOCUMENT = 'document',
    FOLDER = 'folder',
}

export enum MediaSource {
    GOOGLE_DRIVE = 'drive',
    LUMDRIVE = 'local',
    MICROSOFT = 'onedrive',
    COMMUNITY = 'community',
    HAUSSMANN_MEDIA = 'haussmann_media',
    STORAGE = 'storage',
}

export enum MediaSourceV2 {
    GOOGLE_DRIVE = 'google_drive',
    MICROSOFT = 'one_drive',
    HAUSSMANN_MEDIA = 'haussmann_media',
}

export type MediaSourceV2Drive = MediaSourceV2.GOOGLE_DRIVE | MediaSourceV2.MICROSOFT;

export enum MediaStatus {
    LIVE = 'live',
    DELETED = 'deleted',
}

export enum ContainerType {
    COMMUNITY = 'community',
    ORGANIZATION = 'organization',
    SITE = 'site',
}

export enum LibraryType {
    DEFAULT = 'default',
    MANAGED = 'managed',
}

export interface CommunityMedia {
    /** Name of the media. */
    name: string;
    /** The url of the media. */
    url: string;
    /** Whether the media is private or not. */
    isPrivate: boolean;
    /** Id of the media. */
    mediaId: string;
    /** Id of the media's organization. */
    organizationId: string;
    /** Id of the media's folder. */
    folderId: string;
    /** Id of the media's container. */
    containerId: string;
    /** Container type of the media */
    containerType: ContainerType;
    /** Library type of the media */
    libraryType?: LibraryType;
    /** The user id of who uploaded the media. */
    createdBy: string;
    /** The user who uploaded the media. */
    createdByDetails: UserReference;
    /** The date the media has been created. */
    createdAt: string;
    /** The user id of who updated the media. */
    updatedBy: string;
    /** The user who updated the media. */
    updatedByDetails: UserReference;
    /** The date the media has been updated. */
    updatedAt: string;
    /** The mimeType of the media. */
    mimeType: string;
    /** The size of the media, in bytes. */
    size: number;
    /** Focal point coordinates. */
    focalPoint: FocusPoint;
    /** The source of the media */
    source?: MediaSource;
    /** The height of the media, useful only for images. */
    height: number;
    /** The width of the media, useful only for images. */
    width: number;
}

/**
 * Represents the content of a media item.
 */
export interface MediaContent extends BaseFileContent {
    /** The lang of the media content. */
    lang: string;
    /** The mimeType of the media content. */
    mimeType: string;
    /** The url of the media content. */
    url: string;
    /** The public url of the media content. Should be use to display a media. Can be empty for old medias. */
    servingUrl: string;
    /** The url to use when you want to download the media. */
    downloadUrl: string;
    /** Name of the media content. */
    name: string;
    /** The extension of the media content. */
    ext: string;
    /** The user who uploaded the media content. */
    owner: MinimumViableUser<'email' | 'fullName'> & {
        userId: string;
    };
    /** Id of the media content. For old medias, you should use uid and uuid as fallbacks. */
    fileId: string;
    /** The media type, to determine if it is an image or not. */
    type: MediaType;
    /** The size of the media, in bytes. */
    size: number;
    /** The height of the media, useful only for images. */
    height: number;
    /** The width of the media, useful only for images. */
    width: number;
    /** The blobkey of the media. */
    value: string;
    /** Focal point coordinates. */
    focalPoint: FocusPoint;
    /** Crop info */
    cropInfo?: CropInfo;
    // Fields specific to MediaContent
    /** Id of the media content, use it as fallback since this field is used for old medias. */
    uid: string;
    /** Id of the media content, use it as fallback since this field is used for old medias. */
    uuid: string;
    /** Description of the media content. Can be used as the alt HTML attribute. */
    description?: string;
    /** The source of the media */
    source?: MediaSource;
    /** The Media creation date */
    createdAt?: string;
}

/**
 * Base interface for common asset properties.
 */
export interface BaseMediaDocument {
    /** The email of the asset's author. */
    author: string;
    /** The underlying file's metadata. Empty if the asset is a folder. */
    content?: BaseFileContent[];
    /** The metadata of the cropped version of the file. */
    croppedContent?: BaseFileContent[];
    /** The date and time when the asset was created (ISO 8601 date-time string). */
    createdAt?: string;
    /** The ID of the customer. */
    customer?: string;
    /** The asset's description. */
    description?: TranslatableObject<string> | TranslateObject;
    /** Indicates if the asset has cropped content. */
    hasCroppedContent?: boolean;
    /** True if the asset is a folder. */
    isFolder: boolean;
    /** The asset name. */
    name?: TranslatableObject<string> | TranslateObject;
    /** The asset properties (free-form key-value pairs). */
    properties?: Record<string, any>;
    /** The source of the asset. */
    source?: string | MediaSource;
    /** The tags associated with the asset. */
    tags?: string[];
    /** The thumbnail url of the asset. */
    thumbnail?: TranslatableObject<string> | Record<string, string>;
    /** The date and time when the asset was last updated (ISO 8601 date-time string). */
    updatedAt: string;
    /** The unique identifier of the asset. */
    uid: string;
    /** Another unique identifier of the asset. */
    uuid: string;
    /** The path to fetch the asset folder. */
    docPath?: string;
}

export interface Media extends BaseMediaDocument {
    /** List of the available contents for the media, each entry is a variant of the media based on a language. */
    content?: MediaContent[];
    /** The content of the cropped media, if it has been cropped. */
    croppedContent?: MediaContent[];
    /** The provider of the media. (only used for backward compatibility) */
    from: MediaSource;
    /** The provider of the media. Warning: you should always use the `from` field as a fallback for backward compatibility. */
    source: MediaSource;
    thumbnail: Record<string, string>;
    override?: {
        /** The name of the media, by language */
        name: Record<string, string>;
        /** The description of the media, by language */
        description?: Record<string, string>;
    };
    name?: TranslateObject;
    description?: TranslateObject;
    /** The Media tree structure */
    path?: { depth: number; folderName: TranslateObject; folderUid: string }[];
}

/**
 * Represents a drive media, from google drive or one drive
 */
export interface DriveMedia {
    /** The mimeType of the media. */
    mimeType: string;
    /** The url of the media. */
    url?: string;
    /** Should be use to display the media in an iframe. */
    embedUrl?: string;
    /** Name of the media. */
    name: string;
    /** The media type, to determine if it is an image or not. */
    type: MediaType;
    /** Id of the media. */
    id: string;
    /** The source of the media */
    source: MediaSourceV2Drive;
    thumbnails?: {
        url: string;
        width: number;
        height: number;
    }[];
    /** The media creation date */
    createdAt?: string;
    /** The media updated date */
    updatedAt?: string;
}

export interface MediaIcon {
    icon: string;
    color: ColorPalette;
    colorVariant?: ColorVariant;
}

export interface UrlPatterns {
    dailymotion: string[];
    vimeo: string[];
    youtube: string[];
}

export type VideoWebsites = keyof UrlPatterns;

export type MediaPickerSources = Exclude<
    MediaSource,
    MediaSource.COMMUNITY | MediaSource.HAUSSMANN_MEDIA | MediaSource.STORAGE
>;

export interface MediaPickerParams {
    allowedFileTypes: string[];
    allowedProviders: MediaSource[];
    multi: boolean;
}

export type FileListFieldType = 'thumbnail' | 'name' | 'tags' | 'description';
export type FieldsRecord = Partial<Record<FileListFieldType, boolean>>;

export interface UploadURL {
    mediaId: string;
    formDataField: string;
    headers: any;
    httpMethod: string;
    uploadUrl: string;
}

export interface UploadedMedia {
    url?: string;
    mediaId: string;
    organizationId: string;
    containerId: string;
    containerType: string;
    message?: string;
    id?: string;
}

export type WithBlob = {
    /**
     * Temporary URL using the blob URL scheme (used as a fallback on image upload to get around backend v1 sync issues)
     */
    blobUrl?: string;
};
